import type { Json, SiteConfig } from "../types";
import { closestClickable, matchAnchorClick, matchButtonClick } from "../core/cta_matcher";

export type Placement = "header" | "footer" | "nav" | "main";

const FOOTER_RE = /footer|contentinfo/i;
const HEADER_RE = /(^|[^a-z])header([^a-z]|$)|banner|topbar|top-bar/i;
const NAV_RE = /(^|[^a-z])nav([^a-z]|$)|menu/i;

/**
 * Where on the page the clicked element lives. Semantic tags and ARIA
 * landmarks first, then class/id name hints (Tilda / Bitrix / WordPress
 * themes rarely use <footer>). Lets reports exclude footer phone/messenger
 * links that sit on every page and are not a deliberate CTA.
 */
export function detectPlacement(el: Element | null): Placement {
  let cur: Element | null = el;
  let depth = 0;
  while (cur && cur !== document.body && cur !== document.documentElement && depth < 40) {
    const tag = cur.tagName;
    const role = (cur.getAttribute("role") || "").toLowerCase();
    if (tag === "FOOTER" || role === "contentinfo") return "footer";
    if (tag === "HEADER" || role === "banner") return "header";
    if (tag === "NAV" || role === "navigation") return "nav";
    if (!spansWholePage(cur)) {
      const hint = (cur.id || "") + " " + (typeof cur.className === "string" ? cur.className : "");
      if (FOOTER_RE.test(hint)) return "footer";
      if (HEADER_RE.test(hint)) return "header";
      if (NAV_RE.test(hint)) return "nav";
    }
    cur = cur.parentElement;
    depth++;
  }
  return "main";
}

/**
 * A class/id hint on a wrapper as tall as the whole page says nothing about
 * where the click was. Themes put state classes on <body> and page wrappers
 * (OceanWP "has-fixed-footer", Astra "ast-hfb-header", "sticky-responsive-menu"),
 * and every click on such a site used to be labelled footer / header / nav —
 * footer clicks are not counted as conversions, so a whole site showed zero.
 * <body> itself is never inspected: the walk in detectPlacement stops below it.
 */
function spansWholePage(el: Element): boolean {
  try {
    const docH = Math.max(document.documentElement.scrollHeight, document.body?.scrollHeight || 0);
    return docH > 0 && el.getBoundingClientRect().height >= docH * 0.9;
  } catch {
    return false;
  }
}

/** Vertical position of the element as a share of full document height, 0..1. */
function yRatio(el: Element): number | null {
  try {
    const docH = Math.max(document.documentElement.scrollHeight, document.body?.scrollHeight || 0);
    if (!docH) return null;
    const top = el.getBoundingClientRect().top + (window.scrollY || window.pageYOffset || 0);
    return Math.round(Math.max(0, Math.min(1, top / docH)) * 100) / 100;
  } catch {
    return null;
  }
}

export interface ClickTrackerOptions {
  emit: (eventName: string, props: Record<string, Json>) => void;
  getConfig: () => SiteConfig | null;
  /** Active time on the current page at click time — lets reports split
   *  "before conversion" from "after conversion" engagement. */
  getEngagedMs?: () => number;
  /** Max scroll depth (0..100) reached on the current page at click time. */
  getScrollDepth?: () => number;
}

export function installClickTracker(opts: ClickTrackerOptions): () => void {
  function handler(ev: MouseEvent) {
    // Ignore programmatic / right / middle clicks for now.
    if (ev.defaultPrevented) return;
    if (ev.button !== undefined && ev.button !== 0) return;

    const config = opts.getConfig();
    if (config && config.tracking && config.tracking.clicks === false) return;

    const target = closestClickable(ev.target);
    if (!target) return;

    const currentHost = location.host;

    let match;
    if (target instanceof HTMLAnchorElement) {
      match = matchAnchorClick(target, config, currentHost);
    } else if (target instanceof HTMLButtonElement) {
      match = matchButtonClick(target, config);
    } else {
      match = null;
    }

    if (!match) return;
    const props: Record<string, Json> = { ...(match.props as Record<string, Json>) };
    props.placement = detectPlacement(target);
    const ratio = yRatio(target);
    if (ratio !== null) props.y_ratio = ratio;
    if (opts.getEngagedMs) props.engaged_ms = Math.round(opts.getEngagedMs());
    if (opts.getScrollDepth) props.scroll_depth = opts.getScrollDepth();
    opts.emit(match.event_name, props);
  }

  document.addEventListener("click", handler, { capture: true });
  return () => document.removeEventListener("click", handler, { capture: true } as EventListenerOptions);
}
